import { beTableAsComponent } from 'components/BeTable';
import Date, { DATE_TIME } from 'components/Date';
import ProcessingButton from 'components/ProcessingButton';
import TriStateCheckbox, { ALL, NONE, PARTIAL, TriStateCheckboxState } from 'components/TriStateCheckbox';
import { cast, distinct, renderIf, useConsistentState, useEventEmitter } from 'component_utils/utils';
import { ISimpleUserDetails, IProductionResource, IIProductionDao$ISaveTimeWorkedFlushingResult, IItem } from 'interfaces';
import { IProductionWorkTime } from 'interfaces/entities-production';
import { fromPairs, omit, sortBy } from 'lodash';
import moment from 'moment';
import React, { FC, MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaEdit, FaExclamationTriangle, FaSave, FaSync, FaTrash } from 'react-icons/fa';
import { ButtonGroup, UncontrolledTooltip } from 'reactstrap';
import { getLeftPaddingForLevel } from 'styling/stylingVars';
import { ApiLoaderFun, useApi, useApiLoader } from 'utils/API';
import { IWorkOrderStepMap } from 'utils/endpoints/Production';
import Translation from 'utils/Language/Translation';
import { addNotification } from 'utils/NotificationManager';
import Editor, { workedTimeEventEmitter } from './Editor';
import SimpleModal, { ModalRef } from 'components/SimpleModal';
import If from 'components/If';
import { confirm } from 'utils/Prompts';
import { useSpecializations } from 'utils/SpecializationProvider';
import ApplicationTitleAppendix from 'components/ApplicationTitleAppendix';
import Scanner from 'components/Scanner';
import Item from 'components/objects/item';

declare global {
  interface Window {
    be_open_work_time_modal: () => any;
    be_open_time_confirmation: (data: any) => Promise<any>;
  }
}

interface CIProductionWorkTime extends IProductionWorkTime {
  workOrderVisualCode: string;
  targetItem: IItem;
}

const getWorkTimes: ApiLoaderFun<IProductionWorkTime[]> = async (api) =>
  sortBy(await api.production.getWorkTimes(), (it) => it.endTime || moment().toDate()).reverse();
const getWorkTimesForEveryone: ApiLoaderFun<IProductionWorkTime[]> = async (api) =>
  sortBy(await api.production.getAllWorkTimes(), (it) => it.endTime || moment().toDate()).reverse();
const getStateForValues = (values: boolean[], ifEmpty: TriStateCheckboxState): TriStateCheckboxState => {
  if (values.length === 0) {
    return ifEmpty;
  }

  const every = values.every((it) => it);
  const some = values.some((it) => it);
  if (every) {
    return ALL;
  } else if (some) {
    return PARTIAL;
  } else {
    return NONE;
  }
};

const generateTable = (all: boolean, allowFlush: boolean) => {
  interface TableProps {
    errors: IIProductionDao$ISaveTimeWorkedFlushingResult[];
    reload: any;
    saveEntry: (entry: CIProductionWorkTime) => any;
    workOrderSteps: IWorkOrderStepMap;
    workCenters: IProductionResource[];
    checked: any;
    setChecked: (v: any) => any;
  }

  const specializationColumns = window
    ?.be_specializations
    ?.augmentations
    ?.production
    ?.timeBooking
    ?.extraColumns
    ?? []

  return beTableAsComponent<CIProductionWorkTime, TableProps>({
    tableId: `workConfirmationTable-${all}`,
    ignoreTableIdCheck: true,
    listLayoutBelow: 'md',
    getRowClasses: ({ row, props: { errors } }) => {
      if (errors.find(it => it.draftCode === row.code)) {
        return ['text-danger']
      }
      return []
    },
    columns: [
      {
        Header: ({ rows, checked, setChecked, isListLayout }) => {
          if (allowFlush) {
            const values = rows.map((it) => checked[it.code] as boolean);
            const state = getStateForValues(values, NONE);
            return (
              <>
                <If cond={!isListLayout}>
                  <TriStateCheckbox
                    state={state}
                    onChange={(e) => {
                      e.stopPropagation();
                      setChecked((old: any) => {
                        if (state === NONE) {
                          return fromPairs(rows.map((it) => [it.code, true]));
                        } else {
                          return {};
                        }
                      });
                    }}
                  />
                </If>
                <Translation name="T.objects.workConfirmation.workOrderCode" />
              </>
            );
          }
          return <Translation name="T.objects.workConfirmation.workOrderCode" />;
        },
        value: (row) => row.workOrderVisualCode,
        Cell: ({ level, row, checked, setChecked, errors }) => {
          let checkbox = null;
          if (allowFlush) {
            const values = [checked[row.code] as boolean];
            const state = getStateForValues(values, NONE);
            checkbox = (
              <TriStateCheckbox
                state={state}
                onChange={(e) => {
                  e.stopPropagation();
                  setChecked((old: any) => {
                    const gen = { ...old };
                    gen[row.code] = state === NONE ? true : false
                    return gen;
                  });
                }}
              />
            );
          }

          const error = errors.find(it => it.draftCode === row.code)
          let errorComponent = null
          if (error) {
            const target = "error_tooltipl_" + row.code
            errorComponent = (
              <>
                <FaExclamationTriangle id={target}/>
                <UncontrolledTooltip placement="top" target={target}>
                  {error.errorMessage}
                </UncontrolledTooltip>
              </>
            )
          }

          return (
            <div style={{ paddingLeft: getLeftPaddingForLevel(level) }}>
              {checkbox}
              {errorComponent}
              {row.workOrderVisualCode}
            </div>
          );
        },
        filterable: true,
      },
      {
        Header: <Translation name="T.objects.workConfirmation.producedItem" />,
        value: row => row.targetItem.code + " - " + row.targetItem.name,
        Cell: ({ row }) => <Item inline obj={row.targetItem}/>,
        filterable: true
      },
      {
        Header: () => <Translation name="T.objects.workConfirmation.workOrderStep" />,
        value: (row) => row.workOrderStep,
        Cell: ({ row, workOrderSteps }) => {
          const step = (workOrderSteps[row.workOrderCode] || []).find((it) => it.stepCode === row.workOrderStep);
          return (
            <>
              {step ? `${step.stepNumber} - ${step.description}` : row.workOrderStep}
            </>
          );
        },
        filterable: true,
      },
      {
        Header: () => <Translation name="T.objects.workConfirmation.description" />,
        value: (row) => row.description,
        filterable: true,
      },
      {
        Header: () => <Translation name="T.objects.workConfirmation.user" />,
        value: (row) => cast<ISimpleUserDetails>(cast<any>(row).user).username,
        filterable: true,
        show() {
          return all
        },
      },
      {
        Header: () => <Translation name="T.objects.workConfirmation.workCenter" />,
        value: (row) => row.workCenter,
        Cell: ({ row, workCenters }) => {
          if (!row.workCenter) {
            return <>{'-'}</>
          }
          const wc = workCenters.find((it) => it.code === row.workCenter);
          return <>{wc ? `${wc.visualCode} - ${wc.name}` : row.workCenter}</>;
        },
        filterable: true,
      },
      {
        Header: () => <Translation name="T.objects.workConfirmation.startTime" />,
        value: (row) => row.startTime,
        Cell: ({ row }) => <Date val={row.startTime} format={DATE_TIME} />,
        filterable: true,
      },
      {
        Header: () => <Translation name="T.objects.workConfirmation.endTime" />,
        value: (row) => row.endTime || '',
        Cell: ({ row, reload }) => {
          const api = useApi();
          if (!row.endTime) {
            return (
              <ProcessingButton
                color="danger"
                block
                className="py-0"
                onClick={async () => {
                  await api.production.saveWorkTimes({
                    ...cast<IProductionWorkTime>(omit(row, 'user')),
                    endTime: moment().toDate(),
                  });
                  reload();
                }}
              >
                <Translation name="T.misc.stop" />
              </ProcessingButton>
            );
          }
          return <Date val={row.endTime} format={DATE_TIME} />;
        },
        filterable: true,
      },
      ...specializationColumns,
      {
        Header: () => <Translation name="T.objects.workConfirmation.totalTime" />,
        Cell: ({ row }) => {
          let minutes = null;
          minutes = moment.duration(
            (row.endTime ? moment(row.endTime) : moment()).diff(moment(row.startTime))
          ).asMinutes();
          return (
            !isNaN(minutes) &&
            minutes !== null && <Translation name="T.misc.minutes" params={{ minutes: minutes?.toFixed(0) }} />
          );
        },
        className: 'text-right',
      },
      {
        Header: () => <Translation name="T.misc.actions"/>,
        Cell: ({ row, reload, saveEntry }) => {
          const api = useApi();
          return (
            <ButtonGroup className='w-100 py-0'>
              <Editor
                trigger={(open) => (
                  <ProcessingButton color="info" className="py-0" onClick={open}>
                    <FaEdit />
                  </ProcessingButton>
                )}
                getTemplate={() => {
                  const pwt = row;

                  return {
                    code: pwt.code,
                    workOrderCode: pwt.workOrderCode,
                    workOrderStep: pwt.workOrderStep,
                    licensePlate: pwt.licensePlate,
                    description: pwt.description,
                    workCenter: pwt.workCenter,
                    produced: pwt.produced,
                    scrap: pwt.scrap,
                    workTimeType: pwt.workTimeType,
                    startTime: moment(pwt.startTime).toDate(),
                    endTime: pwt.endTime ? moment(pwt.endTime).toDate() : null,
                    userdata: pwt.userdata,
                  };
                }}
              />
              <If cond={allowFlush}>
                <ProcessingButton
                  color="success"
                  className="py-0"
                  onClick={async () => {
                    await saveEntry(row)
                    reload();
                  }}
                >
                  <FaSave />
                </ProcessingButton>
              </If>
              <ProcessingButton
                color="danger"
                className="py-0"
                onClick={async () => {
                  if (await confirm(<Translation name="T.warnings.areYouSureYouWantToDelete"/>)) {
                    await api.production.deleteWorkTimes(row.code);
                    reload();
                  }
                }}
              >
                <FaTrash />
              </ProcessingButton>
            </ButtonGroup>
          );
        },
        width: 120 + (allowFlush ? 40 : 0),
        fixedWidth: true,
        resizable: false,
      },
    ],
  });
};

const View: FC<{ all?: boolean; allowFlush?: boolean }> = ({ all, allowFlush }) => {
  const {
    data: {
      worked, workOrderSteps, workCenters
    },
    isLoading: isLoadingWorked,
    reload,
  } = useApiLoader(
    useCallback(
      async api => {
        const getter = all ? getWorkTimesForEveryone : getWorkTimes
        const worked = await getter(api)
        const workCenters = await api.production.getResources()
        const workOrders = await api.production.getWorkOrders(distinct(worked.map(it => it.workOrderCode)))
        const workOrderSteps = await api.production.getWorkOrderStepsForMultipleAsMap(workOrders.map((it) => it.code));
        
        const completedWorked = worked.map(it => ({
          ...it,
          workOrderVisualCode: workOrders.find(wo => wo.code === it.workOrderCode)?.visualCode,
          targetItem: workOrders.find(wo => wo.code === it.workOrderCode)?.producedItem
        }))

        return {
          workOrders,
          workCenters,
          workOrderSteps,
          worked: completedWorked
        }
      },
      [all]
    ), 
    {
      worked: [],
      workOrders: [],
      workCenters: [],
      workOrderSteps: {}
    }, 
    true
  );
  const [checked, setChecked] = useState<any>({});
  const [errors, setErrors] = useState<IIProductionDao$ISaveTimeWorkedFlushingResult[]>([])
  const specialization = useSpecializations()
  const api = useApi();
  useEventEmitter(workedTimeEventEmitter, 'reload_worked_times', reload)

  const Table = useMemo(() => {
    return generateTable(all, allowFlush);
  }, [all, allowFlush]);

  const flushRecords = async (codes: string[]) => {
    const allResults = await api.production.flushWorkTimes(codes);
    const errResults = allResults.filter(it => !it.saved)
    if (errResults.length > 0) {
      addNotification('danger', <Translation name="T.errors.operationCompletedWithErrors"/>)
    }
    setErrors(errResults)
    reload();
    await specialization?.augmentations?.production?.timeBooking?.afterFlush?.(allResults)
  }

  return (
    <div className="flex-container">
      <div className='focusableHidden'>
        <Scanner placeholder="" onEnter={() => {}}/>
      </div>
      <ApplicationTitleAppendix>
        <FaSync onClick={reload}/>
      </ApplicationTitleAppendix>
      <div className={'nested-flex-container'}>
        <Table
          loading={isLoadingWorked}
          data={worked}
          getExtraProps={() => ({
            errors,
            reload,
            workOrderSteps,
            workCenters,
            checked,
            setChecked,
            async saveEntry(entry) {
              if (!await confirm(<Translation name="T.misc.areYouSure?"/>)) {
                return;
              }

              if (!entry.endTime) {
                await api.production.saveWorkTimes({
                  ...cast<IProductionWorkTime>(omit(entry, 'user')),
                  endTime: moment().toDate(),
                });
                reload();
              }

              await flushRecords([entry.code])
            },
          })}
        />
      </div>

      <ButtonGroup className="w-100">
        <Editor
          trigger={(open) => (
            <ProcessingButton color="primary" onClick={open}>
              <Translation name="T.misc.add" />
            </ProcessingButton>
          )}
          getTemplate={() => ({ workTimeType: 'WORK', description: '', startTime: moment().toDate(), userdata: {} } as IProductionWorkTime)}
        />
        {allowFlush && (
          <ProcessingButton
            color="secondary"
            onClick={async () => {
              const selectedLines = worked.filter((it) => checked[it.code]);
              await flushRecords(selectedLines.map((it) => it.code))
            }}
          >
            <Translation name="T.misc.flush" />
          </ProcessingButton>
        )}
        {renderIf(
          specialization?.augmentations?.production?.timeBooking?.extraActionButtons,
          {
            reload,
            isWorkVerification: all,

            worked,
            workOrderSteps,
            workCenters,
            checked,
            setChecked,
          }
        )}
      </ButtonGroup>
    </div>
  );
};

export const WorkConfirmationAdminView = () => {
  return <View all allowFlush />;
};

export const WorkConfirmationUserView = () => {
  return <View />;
};

export let openWorkTimeModal = () => {}
export let triggerWorkTimeRegistration = async (data: any) => {}
window.be_open_time_confirmation = (data) => triggerWorkTimeRegistration(data)
window.be_open_work_time_modal = () => openWorkTimeModal()

export const WorkConfirmationUserViewModal: FC<{ innerRef: MutableRefObject<ModalRef> }> = (({ innerRef }) => {
  const ref = useRef<ModalRef>();
  const editorModalRef = useRef<ModalRef>();
  const api = useApi()

  const [, getTemplate, setTemplate] = useConsistentState<IProductionWorkTime>(null)

  useEffect(() => {
    openWorkTimeModal = () => ref.current?.toggleOpen?.()
    triggerWorkTimeRegistration = async (data) => {
      const currentRunning = await api.production.getWorkTimes()
      const hasOpenForWoAndStep = currentRunning.find(it => !it.endTime && it.workOrderCode === data.workOrderCode && it.workOrderStep === data.workOrderStep)
      
      let template = {} as IProductionWorkTime
      if (hasOpenForWoAndStep) {
        const pwt = hasOpenForWoAndStep;
        template = {
          code: pwt.code,
          workOrderCode: pwt.workOrderCode,
          workOrderStep: pwt.workOrderStep,
          licensePlate: pwt.licensePlate,
          description: pwt.description,
          workCenter: pwt.workCenter,
          produced: pwt.produced,
          scrap: pwt.scrap,
          workTimeType: pwt.workTimeType,
          startTime: moment(pwt.startTime).toDate(),
          endTime: pwt.endTime ? moment(pwt.endTime).toDate() : null,
          userdata: pwt.userdata,
        }
      } else {
        // new time recording
        const workOrder = (await api.production.getWorkOrders([data.workOrderCode]))[0];
        const workOrderSteps = await api.production.getWorkOrderSteps(workOrder.code)
        const workCenter = data.workOrderWorkCenter || workOrderSteps.find(it => it.stepCode === data.workOrderStep).plannedWorkCenterCode
        template = { 
          workOrderCode: data.workOrderCode,
          workOrderStep: data.workOrderStep,
          workCenter: workCenter,
          workTimeType: 'WORK', 
          description: data.description || '', 
          startTime: moment().toDate(), 
          userdata: {}
        } as IProductionWorkTime
      }
      await setTemplate(template)
      editorModalRef.current.toggleOpen();
    }
  })
  return (
    <>
      <Editor
        modalRef={editorModalRef}
        trigger={() => <></>}
        getTemplate={() => {
          return getTemplate.current;
        }}
      />
      <SimpleModal
        title={<Translation name="T.menu.timeMeasurements" />}
        fullscreen
        ref={e => {
          innerRef.current = e;
          ref.current = e;
        }}
      >
        <WorkConfirmationUserView  />
      </SimpleModal>
    </>
  )
})